"use client";

// Live bell feed — GET /api/admin/alerts (newest first).
// Bell events are immutable; the only write here is resolving an alert.

import { adminApi, type AdminAlert, type PageMeta } from "../lib/api";
import { bellTint, humanize, personName } from "../lib/adminMap";
import { useApi } from "../lib/useApi";
import Icon, { type IconName } from "./Icon";
import { Skeleton } from "./Skeleton";
import { PanelEmpty, PanelError } from "./PanelState";

const CATEGORY_ICON: Record<string, IconName> = {
  CUSTOMER_ON_LOT: "lot",
  NEED_MANAGER_HELP: "handshake",
  MANAGER_ENGAGED: "handshake",
  COUNTER_OFFER: "tag",
  ON_TEST_DRIVE: "car",
  TEST_DRIVE_ABOUT_TO_ARRIVE: "car",
  CUSTOMER_LEAVING: "leaving",
  ESCALATION: "bolt",
};

/** Response time is seconds; null means the bell is still unanswered. */
function responseLabel(a: AdminAlert) {
  if (a.responseTime === null || a.responseTime === undefined) {
    return a.resolvedAt ? "resolved" : "awaiting";
  }
  const s = Math.round(a.responseTime);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function clock(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(11, 16);
}

export function alertIcon(category: string): IconName {
  return CATEGORY_ICON[category] ?? "bell";
}

export default function LiveBellFeed({ limit = 8 }: { limit?: number }) {
  const { data, loading, error, reload } = useApi<{ items: AdminAlert[]; meta: PageMeta }>(
    (signal) => adminApi.alerts({ status: "active", limit, sortOrder: "desc" }, signal),
    [limit],
    "Failed to load bell events.",
  );

  const rows = data?.items ?? [];

  return (
    <div className="glass rounded-[26px] p-5 sm:p-6 flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className="live-dot w-2.5 h-2.5 rounded-full" style={{ background: "var(--danger)" }} />
          <h2 className="font-display text-lg text-text">Live Bells</h2>
        </div>
        <span className="text-[12px] text-text-faint">
          {data?.meta.total !== undefined ? `${data.meta.total} active` : "real-time"}
        </span>
      </div>

      {loading ? (
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-2xl" />
          ))}
        </div>
      ) : error ? (
        <PanelError message={error} onRetry={reload} />
      ) : rows.length === 0 ? (
        <PanelEmpty title="No active bells" hint="New bells appear here the moment they're rung." icon="bell" />
      ) : (
        <ul
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          aria-label="Live bell events"
          className="flex flex-col gap-2.5 overflow-y-auto -mr-2 pr-2"
        >
          {rows.map((a) => {
            const tint = bellTint(a.category);
            const pending = a.responseTime === null && !a.resolvedAt;
            return (
              <li key={a.id} className="flex items-center gap-3 px-3.5 py-3 rounded-2xl">
                <span
                  className="chip grid place-items-center w-9 h-9 rounded-xl shrink-0"
                  style={{ color: tint.color, "--tint-color": tint.pastel } as React.CSSProperties}
                >
                  <Icon name={alertIcon(a.category)} size={17} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-semibold text-text truncate">{humanize(a.category)}</p>
                  <p className="text-[12px] text-text-faint truncate">
                    {a.deal ? `#${a.deal.code} · ` : ""}
                    {personName(a.customer)} · {personName(a.salesperson)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p
                    className="text-[12.5px] font-semibold"
                    style={{ color: pending ? "var(--danger)" : "var(--ok)" }}
                  >
                    {responseLabel(a)}
                  </p>
                  <p className="text-[11px] text-text-faint tabular-nums">{clock(a.createdAt)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
